import React, { FC, useState } from "react";
import {
  Matter,
  ExternalMatter,
  MatterExternalMatterUpdate,
  LibraryFeeEarners,
  MatterNonExternalFieldsUpdate,
  MatterNonExternalFieldsPreUpdate,
  NewClientValue,
  PracticeArea,
  ClientFilterData,
  ExternalMatterData,
  MatterType,
  MatterFormMode,
} from "../../data-types";

import { ExternalMatterSelector } from "./external-matter-selector";
import { MatterFieldsEditor } from "./matter-fields-editor";
import { Collapse, Modal, Switch } from "antd";
import { Spinner } from "../spinner";

const { Panel } = Collapse;

import styles from "./matter-external-or-custom-editor.module.css";
import clsx from "clsx";
import { VPDLabel } from "~/context/taxonomy-context";

const {
  ["wrapper"]: wrapper,
  ["switch"]: switchEl,
  ["subheading"]: subheading,
  ["custom-matter-items-holder"]: customMatterItemsHolder,
  ["active"]: customMatterItemsHolderActive,
  ["external-matter-select-holder"]: externalMatterSelectHolder,
  ["matter-fields-editor-collapsible"]: matterFieldsEditorCollapsible,
} = styles as any;

declare type MatterExternalOrCustomEditorProps<T extends MatterType, U extends MatterFormMode> = {
  className?: string;
  practiceAreas: PracticeArea[];
  externalMatterData: ExternalMatterData;
  feeEarners: LibraryFeeEarners[];
  matter: Matter;
  onClientSave: (newClient: NewClientValue) => void;
  onMatterExternalMatterUpdate: (matter: MatterExternalMatterUpdate) => Promise<void>;
  onMatterUpdate: (matter: MatterNonExternalFieldsUpdate<T, U>) => Promise<void>;
  clientFilterData: ClientFilterData;
  pmsName: string;
};

export const MatterExternalOrCustomEditor = <T extends MatterType, U extends MatterFormMode>({
  className,
  externalMatterData,
  feeEarners,
  matter,
  onClientSave,
  onMatterExternalMatterUpdate,
  onMatterUpdate,
  clientFilterData,
  pmsName,
  practiceAreas,
}: MatterExternalOrCustomEditorProps<T, U>): ReturnType<
  FC<MatterExternalOrCustomEditorProps<T, U>>
> => {
  const PA_LABEL = VPDLabel("PracticeArea");

  const { data: externalMatters } = externalMatterData;

  const { id: matterId, externalMatterId, isParent } = matter;

  const [showUnlinkConfirmation, setShowUnlinkConfirmation] = useState<boolean>(false);
  const [useExternalMatter, setUseExternalMatter] = useState<boolean>(!!externalMatterId);

  const selectedExternalMatter: ExternalMatter | null = externalMatterId
    ? externalMatters.items.filter((m) => m.id === externalMatterId)[0]
    : null;

  const doOnMatterExternalMatterUpdate = ({
    externalMatterId,
  }: {
    externalMatterId: number | null;
  }) => {
    const matter = {
      id: matterId,
      externalMatterId,
      isParent: isParent ?? false,
    };
    return onMatterExternalMatterUpdate(matter);
  };

  const [loading, setLoading] = useState<boolean>(false);

  const doOnMatterUpdate = (matter: MatterNonExternalFieldsPreUpdate<T, U>) => {
    return onMatterUpdate({
      ...matter,
      id: matterId,
    });
  };

  return (
    <div className={`${className} ${wrapper}`}>
      <div
        className={clsx(
          customMatterItemsHolder,
          !useExternalMatter && customMatterItemsHolderActive
        )}
      >
        <span className={subheading}>Link to {pmsName}?</span>
        <Spinner show={loading === true} message={"Updating Matter..."}>
          <Switch
            className={switchEl}
            checked={useExternalMatter}
            onChange={async (useExternalMatterId: boolean) => {
              if (!useExternalMatterId) {
                setShowUnlinkConfirmation(true);
              } else {
                setLoading(true);
                try {
                  await doOnMatterExternalMatterUpdate({
                    externalMatterId: externalMatters.items[0]?.id,
                  });
                  setUseExternalMatter(true);
                } finally {
                  setLoading(false);
                }
              }
            }}
          />
        </Spinner>
        <Modal
          title={`Unlink ${pmsName} Matter`}
          centered
          visible={showUnlinkConfirmation}
          onOk={async () => {
            setLoading(true);
            try {
              await doOnMatterExternalMatterUpdate({
                externalMatterId: null,
              });
              setUseExternalMatter(false);
            } finally {
              setShowUnlinkConfirmation(false);
              setLoading(false);
            }
          }}
          onCancel={() => {
            setShowUnlinkConfirmation(false);
          }}
        >
          <p>
            Are you sure you want to unlink this Matter from the selected {`${pmsName} `}
            Matter?
          </p>
        </Modal>
        <div className={externalMatterSelectHolder}>
          <ExternalMatterSelector
            useExternalMatter={useExternalMatter}
            clientId={matter?.client?.id || null}
            externalMatterData={externalMatterData}
            defaultValue={externalMatterId}
            disabled={!useExternalMatter}
            onMatterExternalMatterUpdate={doOnMatterExternalMatterUpdate}
            pmsName={pmsName}
          />
        </div>
      </div>
      {!useExternalMatter && (
        <Collapse ghost={true} accordion={true} className={matterFieldsEditorCollapsible}>
          <Panel header={`Title, ${PA_LABEL}, etc.`} key="1">
            <MatterFieldsEditor
              enabled={!useExternalMatter}
              feeEarners={feeEarners}
              matter={selectedExternalMatter ? selectedExternalMatter : matter}
              onMatterUpdate={doOnMatterUpdate}
              onClientSave={onClientSave}
              clientFilterData={clientFilterData}
              practiceAreas={practiceAreas}
            />
          </Panel>
        </Collapse>
      )}
    </div>
  );
};
